import { VehicleStockPhoto } from 'src/models';
import { RDWMakes, RDWModels, RDWStyles, RDWVehicle, RDWYears, VehicleColors } from 'src/models/RDWVehicle/RDWVehicle';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { xml2js, ElementCompact } from 'xml-js';
import { SetERAError } from '../Framework/FrameworkActionCreator';
import { AppThunk } from 'src/types';
const soapRequest = require('easy-soap-request');

/** Interfaces */
export interface IRDWVehicleState {
    years: RDWYears[];
    makes: RDWMakes[];
    models: RDWModels[];
    styles: RDWStyles[];
    rdwVehicle: RDWVehicle[];
    modelPhotos: VehicleStockPhoto[];
    vehColors: VehicleColors[];
    rdwMakesBuilt: boolean;
    rdwModelsBuilt: boolean;
    rdwYearsBuilt: boolean;
    rdwStylesBuilt: boolean;
}

/** Initial State */
const initialState: IRDWVehicleState = {
    years: [],
    makes: [],
    models: [],
    styles: [],
    rdwVehicle: [],
    modelPhotos: [],
    vehColors: [],
    rdwMakesBuilt: false,
    rdwModelsBuilt: false,
    rdwYearsBuilt: false,
    rdwStylesBuilt: false,
};

const RDWVehicleSlice = createSlice({
    name: 'RDWVehicle',
    initialState: initialState,
    reducers: {
        setYears(state, action: PayloadAction<RDWYears[]>) {
            state.years = action.payload;
        },
        setMakes(state, action: PayloadAction<RDWMakes[]>) {
            state.makes = action.payload;
        },
        setModels(state, action: PayloadAction<RDWModels[]>) {
            state.models = action.payload;
        },
        setStyles(state, action: PayloadAction<RDWStyles[]>) {
            state.styles = action.payload;
        },
        setVehicles(state, action: PayloadAction<RDWVehicle[]>) {
            state.rdwVehicle = action.payload;
        },
        addPhoto(state, action: PayloadAction<VehicleStockPhoto>) {
            state.modelPhotos.push(action.payload);
        },
        setColors(state, action: PayloadAction<VehicleColors[]>) {
            state.vehColors = action.payload;
        },
        setMakesBuilt(state, action: PayloadAction<boolean>) {
            state.rdwMakesBuilt = action.payload;
        },
        setModelsBuilt(state, action: PayloadAction<boolean>) {
            state.rdwModelsBuilt = action.payload;
        },
        setYearsBuilt(state, action: PayloadAction<boolean>) {
            state.rdwYearsBuilt = action.payload;
        },
        setStylesBuilt(state, action: PayloadAction<boolean>) {
            state.rdwStylesBuilt = action.payload;
        },
    },
});

export const {
    setYears,
    setMakes,
    setModels,
    setStyles,
    setVehicles,
    addPhoto,
    setColors,
    setMakesBuilt,
    setYearsBuilt,
    setModelsBuilt,
    setStylesBuilt,
} = RDWVehicleSlice.actions;

export default RDWVehicleSlice.reducer;

// thunk actions

export function GetVehicleYears(): AppThunk<Promise<void>> {
    return async (dispatch, _getState) => {
        const xmlParams = '<GetVehicleYears xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW" />';
        const response = await callRDWService(xmlParams, 'GetVehicleYears', dispatch);
        if (!!response && !!response.GetVehicleYearsResponse.GetVehicleYearsResult.text) {
            const results = response.GetVehicleYearsResponse.GetVehicleYearsResult.text?.replaceAll('Year', 'year');
            const years = xml2js(results, { compact: true, textKey: 'text' }) as ElementCompact;
            dispatch(setYears(years.NewDataSet.Table));
            dispatch(setYearsBuilt(true));
        }
    };
}

export function GetVehicleMakes(year?: string): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const countryCode = getState().Framework.dealerCountry;
        const yearHigh = new Date().getFullYear();
        const yearTag = year !== undefined && year !== '' ? year : yearHigh.toString();
        const xmlParams = '<GetVehicleMakes xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW">' +
            '<Year>' + yearTag + '</Year>' +
            '<CountryCode>' + countryCode + '</CountryCode>' +
            '</GetVehicleMakes>';
        const response = await callRDWService(xmlParams, 'GetVehicleMakes', dispatch);
        if (!!response && !!response.GetVehicleMakesResponse.GetVehicleMakesResult.text) {
            const results = (response.GetVehicleMakesResponse.GetVehicleMakesResult.text)
                .replaceAll('Make_id', 'makeId')
                .replaceAll('make_name', 'makeName');
            const makes = xml2js(results, { compact: true, textKey: 'text' }) as ElementCompact;
            dispatch(setMakes(makes.NewDataSet.Table));
            dispatch(setMakesBuilt(true));
        }
    };
}

export function GetEraMakes(): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const countryCode = getState().Framework.dealerCountry;
        const yearHigh = new Date().getFullYear();
        const yearLow = yearHigh - 20;
        const xmlParams = '<GetERAVehicleMakes xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW">' +
            '<minYear>' + yearLow + '</minYear>' +
            '<maxYear>' + yearHigh + '</maxYear>' +
            '<CountryCode>' + countryCode + '</CountryCode>' +
            '</GetERAVehicleMakes>';
        const response = await callRDWService(xmlParams, 'GetERAVehicleMakes', dispatch);
        if (!!response && !!response.GetERAVehicleMakesResponse.GetERAVehicleMakesResult.text) {
            const results = (response.GetERAVehicleMakesResponse.GetERAVehicleMakesResult.text)
                .replaceAll('MAKE', 'make');
            const models = xml2js(results, { compact: true, textKey: 'text' }) as ElementCompact;
            dispatch(setMakes(models.NewDataSet.Table));
        }
        dispatch(setMakesBuilt(true));
    };
}

export function GetEraModels(make: string): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const countryCode = getState().Framework.dealerCountry;
        const yearHigh = new Date().getFullYear();
        const yearLow = yearHigh - 20;
        const xmlParams = '<GetERAVehicleCarlines xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW">' +
            '<minYear>' + yearLow + '</minYear>' +
            '<maxYear>' + yearHigh + '</maxYear>' +
            '<make>' + make + '</make>' +
            '<CountryCode>' + countryCode + '</CountryCode>' +
            '</GetERAVehicleCarlines>';
        const response = await callRDWService(xmlParams, 'GetERAVehicleCarlines', dispatch);
        if (!!response && !!response.GetERAVehicleCarlinesResponse.GetERAVehicleCarlinesResult.text) {
            const results = (response.GetERAVehicleCarlinesResponse.GetERAVehicleCarlinesResult.text)
                .replaceAll('CARLINE', 'model');
            // .replaceAll('model_name', 'modelName');
            const models = xml2js(results, { compact: true, textKey: 'text' }) as ElementCompact;
            dispatch(setModels(models.NewDataSet.Table));
        }
        dispatch(setModelsBuilt(true));
    };
}


export function GetEraStyles(year: string, make: string, model: string): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const countryCode = getState().Framework.dealerCountry;
        const xmlParams = '<GetERAVehicleTrims xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW">' +
            '<minYear>' + year + '</minYear>' +
            '<maxYear>' + year + '</maxYear>' +
            '<make>' + make + '</make>' +
            '<carline>' + model + '</carline>' +
            '<CountryCode>' + countryCode + '</CountryCode>' +
            '</GetERAVehicleTrims>';
        const response = await callRDWService(xmlParams, 'GetERAVehicleTrims', dispatch);
        if (!!response && !!response.GetERAVehicleTrimsResponse.GetERAVehicleTrimsResult.text) {
            const results = (response.GetERAVehicleTrimsResponse.GetERAVehicleTrimsResult?.text)
                .replaceAll('TRIM', 'style');
            const styles = xml2js(results, { compact: true, textKey: 'text' }) as ElementCompact;
            const payload = styles.NewDataSet.Table &&styles.NewDataSet.Table.length === undefined ?
                [styles.NewDataSet.Table] :
                styles.NewDataSet.Table;
            dispatch(setStyles(payload));
        }
        dispatch(setStylesBuilt(true));
    };
}

export function GetVehicleModels(year: string, makeId: string): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const countryCode = getState().Framework.dealerCountry;
        const xmlParams = '<GetVehicleModels xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW">' +
            '<Year>' + year + '</Year>' +
            '<makeID>' + makeId + '</makeID>' +
            '<CountryCode>' + countryCode + '</CountryCode>' +
            '</GetVehicleModels>';
        // If there is no makeId clear the models to prevent models from previous make selections
        // from populating the list.
        if (makeId === '' || year === '') {
            dispatch(setModels([]));
        }
        if (makeId) {
            const response = await callRDWService(xmlParams, 'GetVehicleModels', dispatch);
            if (!!response && !!response.GetVehicleModelsResponse.GetVehicleModelsResult.text) {
                const results = (response.GetVehicleModelsResponse.GetVehicleModelsResult.text)
                    .replaceAll('Model_id', 'modelId')
                    .replaceAll('model_name', 'modelName');
                const models = xml2js(results, { compact: true, textKey: 'text' }) as ElementCompact;
                dispatch(setModels(models.NewDataSet.Table));
            }
            dispatch(setModelsBuilt(true));
        }
    };
}

export function GetVehicleStyles(modelId: string): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const countryCode = getState().Framework.dealerCountry;
        const xmlParams = '<GetVehicleStyles xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW">' +
            '<modelID>' + modelId + '</modelID>' +
            '<CountryCode>' + countryCode + '</CountryCode>' +
            '</GetVehicleStyles>';
        const response = await callRDWService(xmlParams, 'GetVehicleStyles', dispatch);
        if (!!response && !!response.GetVehicleStylesResponse.GetVehicleStylesResult.text) {
            const results = (response.GetVehicleStylesResponse.GetVehicleStylesResult.text)
                .replaceAll('style_Id', 'styleId')
                .replaceAll('style_name', 'styleName');
            const styles = xml2js(results, { compact: true, textKey: 'text' }) as ElementCompact;
            dispatch(setStyles(styles.NewDataSet.Table));
        }
        dispatch(setStylesBuilt(true));
    };
}

export function VinDecode(vin: string): AppThunk<Promise<void>> {
    return async (dispatch, _getState) => {
        const xmlParams = '<VinDecode xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW"> ' +
            '<VIN>' + vin + '</VIN>' +
            '</VinDecode>';
        const response = await callRDWService(xmlParams, 'VinDecode', dispatch);
        if (!!response && !!response.VinDecodeResponse.VinDecodeResult.text) {
            const results = (response.VinDecodeResponse.VinDecodeResult.text);
            const vehicles = xml2js(results, { compact: true, textKey: 'text' }) as ElementCompact;
            dispatch(setVehicles(vehicles.NewDataSet.Table));
        }
    };
}

export function GetPhoto(year: string, make: string, model: string): AppThunk<Promise<void>> {
    return async (dispatch, getState) => {
        const userName = 'DMS Application User';
        const pwd = '$Integration';
        // Test when not on SB13;
        const sysId = 'ERA01SB13000000';
        const sysNum = window.location.href.slice(11, 16);
        const sysNum2 = window.location.href.slice(10, 16);
        let MCsysNum = sysNum2;
        if (MCsysNum === 'alhost') {
            MCsysNum = '080027';
        }
        let storeNum = '01';
        let branchNum = '01';

        if (sysNum === '80188') {
            storeNum = '01';
            branchNum = '03';
        }

        // Temp code to force NPD test server to Branch 03
        if (sysNum === '80036') {
            storeNum = '01';
            branchNum = '03';
        }

        const sysType = 'ERA';
        const appCode = 'SVC';
        const countryCode = getState().Framework.dealerCountry;
        const Itype = getState().Framework.ImageServerType;
        const type = window.location.origin.slice(8, 10).toUpperCase();

        let url = 'https://ddwhub.reyrey.net/services/IntegrationService.asmx';
        if (type === 'RD' || type === 'RI') {
            url = 'https://receiver.ddwqa01.reyqa.com/services/IntegrationService.asmx';
        }
        // For 199199 we should point to production and not QA.
        if (sysNum2 === '199199') {
            url = 'https://ddwhub.reyrey.net/services/IntegrationService.asmx';
        }

        if (Itype) {
            if (Itype === '1') {
                url = 'https://ddwhub.reyrey.net/services/IntegrationService.asmx';
            }
            if (Itype === '2') {
                url = 'https://receiver.ddwqa01.reyqa.com/services/IntegrationService.asmx';
            }
            if (Itype === '3') {
                url = 'https://receiver.ddwqa02.reyqa.com/services/IntegrationService.asmx';
            }
            if (Itype === '4') {
                url = 'https://receiver-sb01.reyqa.com/services/IntegrationService.asmx';
            }
            if (Itype === '5') {
                url = 'https://receiver-sb02.reyqa.com/services/IntegrationService.asmx';
            }
        }

        if (sysNum === 'lhost') {
            url = '/services-ddwhub/IntegrationService.asmx';
        }

        const sr = '<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ' +
            'xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">' +
            '<soap:Body>' +
            '<VehicleGetProfilePhoto xmlns="http://reyrey.com/">' +
            '<VehicleCredentials>' +
            '<Username>' + userName + '</Username>' +
            '<Password>' + pwd + '</Password>' +
            '<SysType>' + sysType + '</SysType>' +
            '<SysID>' + sysId + '</SysID>' +
            '<StoreNo>' + storeNum + '</StoreNo>' +
            '<BranchNo>' + branchNum + '</BranchNo>' +
            '<DMSSysNum>' + MCsysNum + '</DMSSysNum>' +
            '<AppCode>' + appCode + '</AppCode>' +
            '</VehicleCredentials>' +
            '<VehicleGetProfilePhotoRequest>' +
            '<Make>' + make + '</Make>' +
            '<Model>' + model + '</Model>' +
            '<Year>' + year + '</Year>' +
            // '<StyleId>' + styleId + '</StyleId>' +
            '<CountryCode>' + countryCode + '</CountryCode>' +
            '</VehicleGetProfilePhotoRequest>' +
            '</VehicleGetProfilePhoto>' +
            '</soap:Body>' +
            '</soap:Envelope>';
        const header = {
            'content-type': 'text/xml; charset=utf-8',
            'SOAPAction': 'http://reyrey.com/VehicleGetProfilePhoto',
            'Access-Control-Allow-Origin': sysNum === 'lhost' ? '*' : window.location.origin,
        };
        const defaultPhoto: VehicleStockPhoto = {
            PhotoAngle: null,
            ThumbnailUrl: null,
            Url: null,
            id: '',
        };
        if (year === '') {
            dispatch(addPhoto(defaultPhoto));
        }
        try {
            const { response } = await soapRequest({ url: url, headers: header, xml: sr, timeout: 10000 });
            const { body, statusCode } = response;
            if (statusCode === 200) {
                const rtn = xml2js(body, { compact: true, textKey: 'text' }) as ElementCompact;
                const soapBody = rtn['soap:Envelope']['soap:Body'];
                const result = soapBody.VehicleGetProfilePhotoResponse.VehicleGetProfilePhotoResult.VehicleStockPhoto;
                dispatch(addPhoto(result as VehicleStockPhoto));
            } else {
                dispatch(addPhoto(defaultPhoto));
            }
        } catch {
            dispatch(addPhoto(defaultPhoto));
        }
    };
}


/** This is the Async call to the RDW Service
 *  This returns undefined or a converted xml2js object
 */

async function callRDWService(
    xmlParams: string,
    requestName: string,
    dispatch: any,
) {
    const userName = 'svpuser';
    const pwd = 'svp_rdw_user';
    const auth = btoa(userName + ':' + pwd);
    const action = 'http://reyrey.com/Reynolds/GenerationsSeries/RDW/' + requestName;

    /* URL routing here is environment-driven (prod vs QA vs localhost) and is sensitive to both
     * `window.location.origin` (sysType: RD/RI/etc) and `window.location.href` (sysNum: lhost/etc).
     * Any change to this logic MUST be tested against ALL supported URL combinations to ensure the
     * correct RDW endpoint is used (prod, QA, and local proxy paths). For more information see:
     * https://wiki.reydev.net/index.php/Service_Portal_2.0_-_Best_Practices_to_Testing_callRDWService
     */
    const sysType = window.location.origin.slice(8, 10).toUpperCase();

    const sysNum = window.location.href.slice(11, 16);
    let url = 'https://desking01.gs.reyrey.com/Reynolds/GenerationsSeries/RDW/v10.0CustomAuth/RDWServiceCustomAuth.asmx';
    let wsaUrl = 'https://desking01.gs.reyrey.com/Reynolds/GenerationsSeries/RDW/v10.0CustomAuth/RDWServiceCustomAuth.asmx';

    if (sysNum === 'lhost') {
        url = '/Reynolds/GenerationsSeries/RDW/v10.0CustomAuth/RDWServiceCustomAuth.asmx';
    }

    if (sysType === 'RD' || sysType === 'RI') {
        url = 'https://dskqa01.reyqa.com/Reynolds/GenerationsSeries/RDW/v10.0CustomAuth/RDWServiceCustomAuth.asmx';
        wsaUrl = 'https://dskqa01.reyqa.com/Reynolds/GenerationsSeries/RDW/v10.0CustomAuth/RDWServiceCustomAuth.asmx';
    }

    const sr = '<soap:Envelope ' +
        'xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ' +
        'xmlns:xsd="http://www.w3.org/2001/XMLSchema" ' +
        'xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/" ' +
        'xmlns:wsa="http://schemas.xmlsoap.org/ws/2004/03/addressing" ' +
        'xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wsswssecurity-secext-1.0.xsd" ' +
        'xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wsswssecurity-utility-1.0.xsd"> ' +
        '<soap:Header>' +
        '<wsa:Action>' + action + '</wsa:Action>' +
        '<wsa:MessageID>uuid:Test</wsa:MessageID>' +
        '<wsa:ReplyTo>' +
        '<wsa:Address>http://schemas.xmlsoap.org/ws/2004/04/addressing/role/anonymous</wsa:Address>' +
        '</wsa:ReplyTo>' +
        '<wsa:To>' + wsaUrl + '</wsa:To>' +
        '<RDWHeader xmlns="http://reyrey.com/Reynolds/GenerationsSeries/RDW">' +
        '<Store>1001</Store>' +
        '<ComputerName>rdwUser</ComputerName>' +
        '<TraceRanking>false</TraceRanking>' +
        '<CompressData>false</CompressData>' +
        '<UseCache>false</UseCache>' +
        '</RDWHeader>' +
        '</soap:Header>' +
        '<soap:Body>' + xmlParams + '</soap:Body>' +
        '</soap:Envelope>';

    const header = {
        'content-type': 'text/xml; charset=utf-8',
        'SOAPAction': action,
        'Authorization': 'Basic ' + auth,
    };

    try {
        const { response } = await soapRequest({ url: url, headers: header, xml: sr, timeout: 10000 });

        const { body, statusCode } = response;

        if (statusCode === 200) {
            const rtn = xml2js(body, { compact: true, textKey: 'text' }) as ElementCompact;
            const soapBody = rtn['soap:Envelope']['soap:Body'];

            return soapBody;
        }
    } catch (error: any) {
        dispatch(SetERAError('Network Error'));

        return null;
    }
}

export function ClearRDWVehicle(): AppThunk<void> {
    return (dispatch) => {
        dispatch(setMakes([]));
        dispatch(setYears([]));
        dispatch(setModels([]));
        dispatch(setStyles([]));
        dispatch(setColors([]));
    };
}